import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { IconButton } from '@parsina/framework';
import { C, RADIUS, SHADOW, SCRIM } from '@parsina/framework/tokens';
import { useTranslation } from '../i18n';
import useOverlayHistory from './useOverlayHistory';
import useVisibleBox from './useVisibleBox';

/**
 * The mobile task overlay.
 *
 * Every form, filter and picker on the phone renders through this, which is
 * the point: at the pinned framework version (v0.10.1) the kit's `BottomSheet`
 * has six separate problems on a real device, and each one would otherwise be
 * fixed — or missed — eight times over.
 *
 * | Kit v0.10.1                     | Here                                    |
 * |---------------------------------|-----------------------------------------|
 * | `max-height: 85vh`              | `85dvh`, `vh` kept as the fallback      |
 * | blind to the software keyboard  | sits inside the visible box (PM-0168)   |
 * | outside the history stack       | `useOverlayHistory`                     |
 * | rendered where it is mounted    | portaled to `<body>`                    |
 * | focus leaks to the page behind  | focus moves in, Tab cycles, opener back |
 * | page scrolls behind it          | body scroll lock                        |
 *
 * `vh` is the LARGEST viewport, so with iOS Safari's address bar expanded an
 * 85vh sheet is taller than the screen. `dvh` fixes that but is still blind to
 * the keyboard: `position: fixed`, `dvh` and `svh` all describe the LAYOUT
 * viewport, and the keyboard is painted over it — so a focused field's submit
 * button ends up under the keys with no CSS-only way to know.
 *
 * **The keyboard (PM-0168, the kit's PM-0108 fix ported).** This sheet used to
 * keep its bottom edge on the LAYOUT viewport and only shrink its ceiling by
 * the keyboard inset — and then pad its footer by the same inset again. On
 * Android Chrome (`interactive-widget=resizes-visual`) the keyboard is painted
 * OVER the layout viewport, so the sheet's lower part sat under the keys, the
 * footer's commit row floated on a keyboard-high slab of padding, and Chrome's
 * own reveal of the focused field PANNED the visual viewport, sliding the title
 * bar off the top of the screen. Now the scrim sits exactly on the box the
 * user can actually see, the sheet takes `snap` of that box, the footer never
 * pads by the keyboard, and the focused field is re-revealed when the box
 * changes — the kit `BottomSheet`'s v0.23.3 idea, measured differently (below).
 *
 * **iPhone (PM-0181).** The kit hook reads the keyboard off `innerHeight`,
 * which iOS Safari shrinks together with the visual viewport, so on an iPhone
 * it reports no keyboard. The sheet therefore takes the visible box straight
 * from `visualViewport` (`useVisibleBox`: top + height, no `innerHeight`) and
 * places its scrim exactly on it. Two more iOS-only rules: a field below 16px
 * makes Safari ZOOM the page on focus (and never zoom back), so every field
 * inside the sheet is held at ≥16px by `SHEET_FIELD_CSS`; and with the
 * keyboard up the home indicator is under the keys, so the footer drops its
 * `safe-area-inset-bottom` pad instead of floating 34px above them.
 *
 * The portal is not tidiness either. On iOS WebKit a `position: fixed`
 * descendant of a momentum scroller is trapped in it: a sheet mounted inside a
 * screen's scroll region is clipped to that region and painted UNDER the
 * screen's docked bar. Chromium does not do this, so no desktop browser test
 * ever sees it.
 *
 * See docs/MOBILE-REDESIGN.md §4.2.
 */
/**
 * iOS Safari zooms the page when a field under 16px takes focus. Inline styles
 * across the forms (`bookInputStyle` is 14px) lose to this on purpose: inside a
 * phone sheet a field is never smaller than 16px (PM-0181).
 */
const SHEET_FIELD_CSS = '[data-mobile-sheet] :is(input, textarea, select) { font-size: max(16px, 1em) !important; }';

/** Room kept around a focused field inside the scroller: its label, its ring. */
const REVEAL_MARGIN = 32;

const SHEET_UNIT =
  typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('height: 1dvh')
    ? 'dvh'
    : 'vh';

export default function MobileSheet({
  open,
  onClose,
  title,
  children,
  footer,
  /** A tall form fills the sheet, so its footer sits at the bottom edge rather than halfway up. */
  fullHeight = false,
  /** Fraction of the viewport the sheet may occupy. `snap={1}` + `fullHeight` is a full-screen task. */
  snap = 0.85,
  closeLabel,
  'data-testid': testId,
  'aria-label': ariaLabel,
  ...rest
}) {
  const { t } = useTranslation();
  // The box the user can see, straight from `visualViewport` (PM-0168, PM-0181).
  const box = useVisibleBox();
  const panInset = box ? box.top : 0;
  const keyboardInset = box ? box.covered : 0;
  // Something covers part of the screen (keyboard, Chrome's pan): the sheet
  // then lives in the visible box. 1px of rounding is not a keyboard.
  const boxed = Boolean(box) && (box.top > 0 || box.covered > 1);
  // A real keyboard, not a toolbar wobble: the home indicator is under it.
  const keyboardUp = Boolean(box) && box.covered > 120;
  const panelRef = React.useRef(null);
  const scrollerRef = React.useRef(null);
  const openerRef = React.useRef(null);
  const titleId = React.useId();

  useOverlayHistory(open, onClose);

  // Remember who opened us, and give focus back on close. A modal that leaves
  // focus on <body> strands a keyboard or screen-reader user at the top of the
  // page they were already halfway down.
  React.useEffect(() => {
    if (!open) return undefined;
    openerRef.current = document.activeElement;
    return () => {
      const opener = openerRef.current;
      if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
        opener.focus();
      }
    };
  }, [open]);

  // Move focus in once the panel exists.
  React.useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;
    const first = panel.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    (first || panel).focus?.({ preventScroll: true });
  }, [open]);

  // Escape closes; Tab cycles inside. Both on the panel's own listener so a
  // nested ConfirmDialog (which sits above us on the z-scale) still wins.
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose?.(); return; }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = [...panel.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )].filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Keep the field being typed into fully inside the sheet's scroller, with
  // room around it — its label above, its focus ring below. Runs when the
  // visible box changes under an open sheet (the keyboard arriving, the
  // browser panning) and on every focus change inside it. `scrollIntoView
  // ('nearest')`, which the kit uses, leaves the field flush against the
  // footer with its lower edge and ring cut off — what the owner saw on the
  // phone field of «بیمار جدید» (PM-0168). Only the sheet's own scroller
  // moves: the panel is inside the visible box by construction.
  const reveal = React.useCallback(() => {
    const panel = panelRef.current;
    const scroller = scrollerRef.current;
    const active = typeof document === 'undefined' ? null : document.activeElement;
    if (!panel || !scroller || !active || active === panel || !scroller.contains(active)) return;
    const box = scroller.getBoundingClientRect();
    const field = active.getBoundingClientRect();
    const below = field.bottom - (box.bottom - REVEAL_MARGIN);
    const above = (box.top + REVEAL_MARGIN) - field.top;
    // Too low: scroll down, but never so far that the field's top (a tall
    // textarea) passes the top margin. Too high: scroll up.
    if (below > 0) scroller.scrollTop += Math.min(below, Math.max(0, -above));
    else if (above > 0) scroller.scrollTop -= above;
  }, []);

  React.useEffect(() => {
    if (!open || typeof requestAnimationFrame !== 'function') return undefined;
    const raf = requestAnimationFrame(reveal);
    // Again once the keyboard animation settles: Chrome's own reveal on the
    // resize scrolls only the CARET into view, after ours, and undoes the margin.
    const settle = setTimeout(reveal, 300);
    return () => { cancelAnimationFrame(raf); clearTimeout(settle); };
  }, [open, panInset, keyboardInset, reveal]);

  React.useEffect(() => {
    const panel = panelRef.current;
    if (!open || !panel || typeof requestAnimationFrame !== 'function') return undefined;
    // After the browser's own focus scroll, which is the flush one.
    const onFocusIn = () => requestAnimationFrame(reveal);
    panel.addEventListener('focusin', onFocusIn);
    return () => panel.removeEventListener('focusin', onFocusIn);
  }, [open, reveal]);

  // Lock the page behind. iOS ignores `overflow: hidden` on its own, so the
  // touch-action and overscroll properties carry the rest of the weight.
  React.useEffect(() => {
    if (!open || typeof document === 'undefined') return undefined;
    const { body } = document;
    const prev = {
      overflow: body.style.overflow,
      touchAction: body.style.touchAction,
      overscrollBehavior: body.style.overscrollBehavior,
    };
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    body.style.overscrollBehavior = 'none';
    return () => {
      body.style.overflow = prev.overflow;
      body.style.touchAction = prev.touchAction;
      body.style.overscrollBehavior = prev.overscrollBehavior;
    };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  const pct = Math.round(Math.max(0.3, Math.min(1, snap)) * 100);
  // With the screen partly covered, the scrim IS the visible box and the sheet
  // may take `snap` of it — the keyboard's height never enters the arithmetic,
  // so neither does `innerHeight`, which the two engines disagree about.
  //
  // Otherwise the old ceiling. The unit is resolved once, at runtime, rather
  // than declared twice: two `maxHeight` keys in one style object is not a CSS
  // cascade, it is an object literal, and the second silently deletes the
  // first. `dvh` is the correct unit (`vh` is the LARGEST viewport, so an 85vh
  // sheet overflows a Safari window whose address bar is expanded); `vh`
  // remains the answer for an engine that has never heard of `dvh`.
  const ceiling = boxed
    ? `${Math.floor((box.height * pct) / 100)}px`
    : `${pct}${SHEET_UNIT}`;

  const sheet = (
    <div
      style={{
        position: 'fixed', zIndex: 1000,
        ...(boxed
          ? { top: `${box.top}px`, left: 0, right: 0, height: `${box.height}px` }
          : { inset: 0 }),
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        background: SCRIM,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid={testId ? `${testId}-scrim` : undefined}
    >
      <style>{SHEET_FIELD_CSS}</style>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title ? undefined : ariaLabel}
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        data-testid={testId}
        data-mobile-sheet=""
        style={{
          width: '100%',
          background: C.card,
          borderTopLeftRadius: RADIUS.hero,
          borderTopRightRadius: RADIUS.hero,
          boxShadow: SHADOW.modal,
          maxHeight: ceiling,
          height: fullHeight ? ceiling : undefined,
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}
        {...rest}
      >
        {(title || onClose) && (
          <header
            style={{
              display: 'flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.75rem 1rem',
              borderBottom: `1px solid ${C.border}`,
              flexShrink: 0,
            }}
          >
            <h2
              id={titleId}
              style={{
                flex: 1, minWidth: 0, margin: 0,
                fontSize: '1rem', fontWeight: 700, color: C.textPrimary,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {title}
            </h2>
            {onClose && (
              <IconButton
                icon={<X size={18} />}
                label={closeLabel || t('common.actions.close')}
                onClick={onClose}
                variant="ghost"
                data-testid={testId ? `${testId}-close` : undefined}
              />
            )}
          </header>
        )}

        <div ref={scrollerRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {children}
        </div>

        {footer && (
          <footer
            style={{
              flexShrink: 0,
              borderTop: `1px solid ${C.border}`,
              padding: '0.75rem 1rem',
              // The sheet already ends at the keyboard's top edge, so the footer
              // never pads by the keyboard (PM-0168) — and with the keyboard up
              // the home indicator is under the keys, so not by the safe area
              // either (PM-0181); iOS would float the commit row 34px above them.
              paddingBottom: keyboardUp
                ? '0.75rem'
                : 'calc(0.75rem + env(safe-area-inset-bottom, 0px))',
              background: C.card,
            }}
          >
            {footer}
          </footer>
        )}
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
}
